import { useCachedActions } from '@hooks/useCachedActions';
import type { FetchData } from '@hooks/useDataFetcher';
import { buildCacheKey } from '@shared/util/cache-utils';
import { useMemo } from 'react';
import type {
  Localization,
  LocalizationListResponseV1,
} from 'types/product-catalog';

import type { SnackbarActions } from './useSnackbar';

interface IState {
  loading: boolean;
}

interface IActions {
  getLocalizations: (
    productId: string,
    locale: string,
  ) => Promise<Localization[]>;
}

export type UseLocalization = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [IState, IActions];

export const useLocalizations: UseLocalization = (makeRequest, snackbar) => {
  const { loading, createAction } = useCachedActions(snackbar);

  const actions = useMemo<IActions>(
    () => ({
      getLocalizations: createAction(
        (productId, locale) =>
          buildCacheKey('getLocalizations', { productId, locale }),
        async (productId: string, locale: string) => {
          const response = await makeRequest<LocalizationListResponseV1>(
            '/api/localizations',
            { id: productId, locale },
          );

          return response.data ?? [];
        },
        [],
      ),
    }),
    [createAction, makeRequest],
  );

  return [{ loading }, actions];
};
